import { useCallback, useEffect, useState } from 'react';
import { apiFetch, type ApiError } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.js';

interface AuthKey {
  id: string;
  label: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

interface KeysResponse {
  keys: AuthKey[];
}

interface CreateKeyResponse {
  key: string;
  expiresAt: string | null;
}

const EXPIRE_OPTIONS = [
  { value: 0, label: 'Kein Ablaufdatum' },
  { value: 7, label: '7 Tage' },
  { value: 30, label: '30 Tage' },
  { value: 90, label: '90 Tage' },
  { value: 365, label: '1 Jahr' },
];

function formatDate(iso: string | null): string {
  if (!iso) return 'Kein Ablaufdatum';
  try {
    return new Date(iso).toLocaleString('de-DE');
  } catch {
    return iso;
  }
}

export default function KeyManager({ onClose }: { onClose: () => void }) {
  const { isBroadcaster } = useAuth();

  const [keys, setKeys] = useState<AuthKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formLabel, setFormLabel] = useState('');
  const [formExpiry, setFormExpiry] = useState(0);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadKeys = useCallback(async () => {
    setLoadingKeys(true);
    setLoadError(null);
    try {
      const data = await apiFetch<KeysResponse>('/api/auth/keys');
      setKeys(data.keys);
    } catch (err) {
      const apiErr = err as ApiError;
      setLoadError(
        apiErr?.status === 403
          ? 'Nur Broadcaster können Keys verwalten.'
          : 'Laden fehlgeschlagen.',
      );
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  useEffect(() => {
    if (!isBroadcaster) return;
    void loadKeys();
  }, [isBroadcaster, loadKeys]);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    setCreateError(null);
    setNewKey(null);
    try {
      const data = await apiFetch<CreateKeyResponse>('/api/auth/key', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: formLabel.trim(),
          expiresInDays: formExpiry === 0 ? null : formExpiry,
        }),
      });
      setNewKey(data.key);
      setFormLabel('');
      setFormExpiry(0);
      await loadKeys();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status === 429) {
        setCreateError('Zu viele Anfragen — bitte warte eine Minute.');
      } else if (apiErr?.status === 400) {
        setCreateError('Ungültige Eingabe. Ablaufzeit: 1–365 Tage oder kein Ablaufdatum. Label max. 100 Zeichen.');
      } else {
        setCreateError('Erstellen fehlgeschlagen.');
      }
    } finally {
      setCreating(false);
    }
  }, [formLabel, formExpiry, loadKeys]);

  const handleDelete = useCallback(
    async (id: string) => {
      setDeletingId(id);
      try {
        await apiFetch(`/api/auth/key/${id}`, { method: 'DELETE' });
        setKeys((prev) => prev.filter((k) => k.id !== id));
        // If the deleted key was the newly shown one, clear it
        setNewKey(null);
      } catch (err) {
        const apiErr = err as ApiError;
        console.error('KeyManager: delete failed', apiErr);
      } finally {
        setDeletingId(null);
      }
    },
    [],
  );

  if (!isBroadcaster) {
    return (
      <div className="p-6 text-slate-300">
        <p>Diese Seite ist nur für den Broadcaster sichtbar.</p>
        <button onClick={onClose} className="mt-3 text-sm text-slate-400 underline">
          Zurück
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
        <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-200">
          &larr; Zurück
        </button>
        <h1 className="text-lg font-semibold">Meld Dock — Auth-Keys</h1>
      </header>

      {/* Create form */}
      <section className="px-4 py-4 border-b border-slate-800">
        <h2 className="text-sm font-semibold text-slate-300 mb-3">Neuen Key erstellen</h2>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-400">Label (optional)</span>
            <input
              type="text"
              value={formLabel}
              onChange={(e) => setFormLabel(e.target.value.slice(0, 100))}
              placeholder="z.B. Meld Studio Desktop"
              className="bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm w-56"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-400">Ablaufzeit</span>
            <select
              value={formExpiry}
              onChange={(e) => setFormExpiry(Number(e.target.value))}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm"
            >
              {EXPIRE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => void handleCreate()}
            disabled={creating}
            className="px-4 py-1.5 rounded bg-purple-700 hover:bg-purple-600 disabled:opacity-50 text-sm"
          >
            {creating ? 'Erstelle…' : 'Key erstellen'}
          </button>
        </div>
        {createError && <p className="mt-2 text-sm text-red-400">{createError}</p>}

        {newKey && (
          <div className="mt-4 p-3 rounded bg-slate-800 border border-purple-700">
            <p className="text-xs text-purple-300 font-semibold mb-1">
              Key — nur jetzt sichtbar, sicher aufbewahren!
            </p>
            <code className="block break-all text-sm text-slate-100 font-mono select-all">
              {newKey}
            </code>
            <p className="mt-2 text-xs text-slate-400">
              Verwende als URL-Parameter: <code className="font-mono">?key={newKey.slice(0, 8)}…</code>
            </p>
          </div>
        )}
      </section>

      {/* Key list */}
      <main className="px-4 py-4">
        <h2 className="text-sm font-semibold text-slate-300 mb-3">Aktive Keys</h2>
        {loadError && <p className="text-sm text-red-400 mb-3">{loadError}</p>}
        {loadingKeys && <p className="text-sm text-slate-500">Lade…</p>}
        {!loadingKeys && keys.length === 0 && (
          <p className="text-sm text-slate-500">Keine aktiven Keys vorhanden.</p>
        )}
        {keys.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-slate-800">
                  <th className="py-2 pr-3">Label</th>
                  <th className="py-2 pr-3">Erstellt</th>
                  <th className="py-2 pr-3">Läuft ab</th>
                  <th className="py-2 pr-3">Zuletzt genutzt</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b border-slate-900 align-top">
                    <td className="py-2 pr-3 text-slate-200">
                      {k.label || <span className="text-slate-500 italic">— kein Label —</span>}
                    </td>
                    <td className="py-2 pr-3 text-slate-400 whitespace-nowrap text-xs">
                      {formatDate(k.createdAt)}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap text-xs">
                      {k.expiresAt === null
                        ? <span className="text-amber-400">Kein Ablaufdatum</span>
                        : <span className="text-slate-400">{formatDate(k.expiresAt)}</span>
                      }
                    </td>
                    <td className="py-2 pr-3 text-slate-400 whitespace-nowrap text-xs">
                      {k.lastUsedAt ? formatDate(k.lastUsedAt) : '—'}
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => void handleDelete(k.id)}
                        disabled={deletingId === k.id}
                        className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                      >
                        {deletingId === k.id ? 'Lösche…' : 'Löschen'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
